import { useUpdateTableViewCellApi } from '@/api/tableViews';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Ellipsis } from '@/components/ui/ellipsis';
import { Input } from '@/components/ui/input';
import { Multiselect } from '@/components/ui/multiselect';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { type TableViewCellDto, type TableViewColumnDto } from '@ditto/shared';
import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { getColorForOption } from '../../utils/cellUtils';

type EditableCellProps = {
  cell: TableViewCellDto;
  column: TableViewColumnDto;
  columns: TableViewColumnDto[];
  rowId: string;
  tableViewId: string;
  onOpenRowPanel?: () => void;
};

// Shared hook for editable cell state with optimistic updates
const useEditableCell = <T,>(serverValue: T, onSave: (value: T) => void) => {
  const [localValue, setLocalValue] = useState<T>(serverValue);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setLocalValue(serverValue);
  }, [serverValue]);

  const startEditing = () => setIsEditing(true);

  const saveAndClose = () => {
    setIsEditing(false);
    if (localValue !== serverValue) {
      onSave(localValue);
    }
  };

  const cancelEditing = () => {
    setLocalValue(serverValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent, allowShiftEnter = false) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    } else if (e.key === 'Enter' && (allowShiftEnter ? !e.shiftKey : true)) {
      e.preventDefault();
      saveAndClose();
    }
  };

  return {
    localValue,
    setLocalValue,
    isEditing,
    startEditing,
    saveAndClose,
    cancelEditing,
    handleKeyDown,
  };
};

// Shared wrapper for non-editing display state
const CellDisplayWrapper: FC<{
  onClick: () => void;
  children: ReactNode;
}> = ({ onClick, children }) => (
  <div
    className="cursor-pointer w-full h-full rounded-lg p-2"
    onClick={e => {
      e.stopPropagation();
      onClick();
    }}
  >
    {children}
  </div>
);

// Two-zone wrapper for text/number cells: click on text = edit, click on empty space = open panel
const CellDisplayWrapperWithZones: FC<{
  onClickText: () => void;
  onClickEmpty?: () => void;
  children: ReactNode;
}> = ({ onClickText, onClickEmpty, children }) => (
  <div
    className="group/cell cursor-pointer w-full h-full rounded-lg p-2"
    onClick={e => {
      e.stopPropagation();
      onClickEmpty?.();
    }}
  >
    <div
      className="cursor-text w-fit rounded px-0.5 -mx-0.5 group-hover/cell:bg-neutral-200/70 transition-colors"
      onClick={e => {
        e.stopPropagation();
        onClickText();
      }}
    >
      {children}
    </div>
  </div>
);

// Shared wrapper for editing state
const CellEditWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="w-full h-full ring-1 ring-inset ring-gray-400 bg-white rounded-lg">
    {children}
  </div>
);

// Hook for cursor positioning on edit start
const useCursorAtEnd = (
  isEditing: boolean,
  valueLength: number,
  ref: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>,
  scrollType: 'vertical' | 'horizontal' = 'vertical'
) => {
  useEffect(() => {
    if (isEditing && ref.current) {
      // setSelectionRange is not supported on input types: number, date, time, etc.
      // Only call it for supported input types (text, search, url, tel, password) and textareas
      const element = ref.current;
      const isTextarea = element.tagName === 'TEXTAREA';
      const supportedInputTypes = ['text', 'search', 'url', 'tel', 'password'];
      const isInput = element.tagName === 'INPUT';
      const inputType = isInput
        ? (element as HTMLInputElement).type
        : undefined;
      const supportsSelection =
        isTextarea ||
        (isInput && supportedInputTypes.includes(inputType ?? ''));

      if (supportsSelection) {
        element.setSelectionRange(valueLength, valueLength);
      }

      if (scrollType === 'vertical') {
        element.scrollTop = element.scrollHeight;
      } else {
        element.scrollLeft = element.scrollWidth;
      }
    }
  }, [isEditing]);
};

// Generic textarea cell for text and verbatim
const EditableTextareaCell: FC<
  EditableCellProps & {
    getValue: (cell: TableViewCellDto) => string;
    patchKey: 'textValue' | 'verbatimValue';
    rows: number;
    lineClamp: number;
    italic?: boolean;
    formatDisplay?: (value: string) => string;
    useZonedWrapper?: boolean;
  }
> = ({
  cell,
  column,
  rowId,
  tableViewId,
  getValue,
  patchKey,
  rows,
  lineClamp,
  italic,
  formatDisplay,
  onOpenRowPanel,
  useZonedWrapper,
}) => {
  const { mutate: updateCell } = useUpdateTableViewCellApi();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const serverValue = getValue(cell);
  const {
    localValue,
    setLocalValue,
    isEditing,
    startEditing,
    saveAndClose,
    handleKeyDown,
  } = useEditableCell(serverValue, value => {
    updateCell({
      tableViewId,
      data: {
        rowId,
        columnId: column.id,
        patch: { [patchKey]: value || null },
      },
    });
  });

  useCursorAtEnd(isEditing, localValue.length, textareaRef, 'vertical');

  if (isEditing) {
    return (
      <CellEditWrapper>
        <Textarea
          ref={textareaRef}
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          onBlur={saveAndClose}
          onKeyDown={e => handleKeyDown(e, true)}
          onClick={e => e.stopPropagation()}
          className={`border-none shadow-none focus-visible:ring-0 h-full p-2 resize-none ${italic ? 'italic' : ''}`}
          rows={rows}
          autoFocus
        />
      </CellEditWrapper>
    );
  }

  const displayValue = formatDisplay
    ? formatDisplay(localValue)
    : localValue || '-';

  const content = (
    <Ellipsis asChild lineClamp={lineClamp} hasTooltip={lineClamp === 1}>
      <Text
        variant="body-sm"
        className={`whitespace-pre-line ${italic ? 'italic' : ''}`}
      >
        {displayValue}
      </Text>
    </Ellipsis>
  );

  if (useZonedWrapper) {
    return (
      <CellDisplayWrapperWithZones
        onClickText={startEditing}
        onClickEmpty={onOpenRowPanel}
      >
        {content}
      </CellDisplayWrapperWithZones>
    );
  }

  return (
    <CellDisplayWrapper onClick={startEditing}>{content}</CellDisplayWrapper>
  );
};

export const EditableTextCell: FC<EditableCellProps> = props => (
  <EditableTextareaCell
    {...props}
    getValue={cell => cell.textValue ?? ''}
    patchKey="textValue"
    rows={5}
    lineClamp={5}
    useZonedWrapper
  />
);

export const EditableVerbatimCell: FC<EditableCellProps> = props => (
  <EditableTextareaCell
    {...props}
    getValue={cell => cell.verbatimValue ?? ''}
    patchKey="verbatimValue"
    rows={1}
    lineClamp={1}
    italic
    formatDisplay={v => (v ? `"${v}"` : '-')}
  />
);

export const EditableNumberCell: FC<EditableCellProps> = ({
  cell,
  column,
  rowId,
  tableViewId,
  onOpenRowPanel,
}) => {
  const { mutate: updateCell } = useUpdateTableViewCellApi();
  const inputRef = useRef<HTMLInputElement>(null);

  const serverValue = cell.numberValue ?? 0;
  const {
    localValue,
    setLocalValue,
    isEditing,
    startEditing,
    saveAndClose,
    handleKeyDown,
  } = useEditableCell(serverValue, value => {
    updateCell({
      tableViewId,
      data: {
        rowId,
        columnId: column.id,
        patch: { numberValue: value },
      },
    });
  });

  useCursorAtEnd(isEditing, String(localValue).length, inputRef, 'horizontal');

  if (isEditing) {
    return (
      <CellEditWrapper>
        <Input
          ref={inputRef}
          type="number"
          value={localValue}
          onChange={e => setLocalValue(Number(e.target.value))}
          onBlur={saveAndClose}
          onKeyDown={handleKeyDown}
          onClick={e => e.stopPropagation()}
          className="border-none shadow-none focus-visible:ring-0 h-8 px-1"
          autoFocus
        />
      </CellEditWrapper>
    );
  }

  return (
    <CellDisplayWrapperWithZones
      onClickText={startEditing}
      onClickEmpty={onOpenRowPanel}
    >
      <Text variant="body-sm">{localValue}</Text>
    </CellDisplayWrapperWithZones>
  );
};

export const EditableDateCell: FC<EditableCellProps> = ({
  cell,
  column,
  rowId,
  tableViewId,
}) => {
  const { mutate: updateCell } = useUpdateTableViewCellApi();
  const [isOpen, setIsOpen] = useState(false);

  const [localDate, setLocalDate] = useState<Date | undefined>(
    cell.dateValue ? new Date(cell.dateValue) : undefined
  );

  useEffect(() => {
    setLocalDate(cell.dateValue ? new Date(cell.dateValue) : undefined);
  }, [cell.dateValue]);

  const handleDateSelect = (date: Date | undefined) => {
    setLocalDate(date);
    updateCell({
      tableViewId,
      data: {
        rowId,
        columnId: column.id,
        patch: { dateValue: date ? date.toISOString().split('T')[0] : null },
      },
    });
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className={`cursor-pointer w-full h-full rounded-lg p-2 transition-all ${
            isOpen ? 'ring-1 ring-inset ring-gray-400 bg-white' : ''
          }`}
          onClick={e => e.stopPropagation()}
        >
          <Text variant="body-sm">
            {localDate ? localDate.toLocaleDateString() : '-'}
          </Text>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
        <Calendar
          mode="single"
          selected={localDate}
          onSelect={handleDateSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export const EditableOptionsCell: FC<EditableCellProps> = ({
  cell,
  column,
  columns,
  rowId,
  tableViewId,
}) => {
  const { mutate: updateCell } = useUpdateTableViewCellApi();
  const [isOpen, setIsOpen] = useState(false);

  // Optimistic local state
  const [localSelectedOptions, setLocalSelectedOptions] = useState(
    cell.selectedOptions ?? []
  );

  // Sync with server
  useEffect(() => {
    setLocalSelectedOptions(cell.selectedOptions ?? []);
  }, [cell.selectedOptions]);

  const handleChange = (selectedValues: string[]) => {
    // Calculate new options for optimistic update
    const availableOptions = column.options ?? [];
    const newSelectedOptions = availableOptions.filter(opt =>
      selectedValues.includes(opt.id)
    );

    // Optimistic update
    setLocalSelectedOptions(newSelectedOptions);

    // Trigger API call
    updateCell({
      tableViewId,
      data: {
        rowId,
        columnId: column.id,
        patch: { selectedOptionIds: selectedValues },
      },
    });
  };

  const multiselectOptions = (column.options ?? []).map(option => ({
    label: option.value,
    value: option.id,
    color: getColorForOption(option.id, columns),
  }));

  const selectedValues = localSelectedOptions.map(o => o.id);

  const dropdownTrigger = (
    <div
      className={`flex flex-wrap gap-1 w-full h-full cursor-pointer rounded-lg p-2 transition-all ${
        isOpen ? 'ring-1 ring-inset ring-gray-400 bg-white' : ''
      }`}
      onClick={e => e.stopPropagation()}
    >
      {localSelectedOptions.length > 0 ? (
        localSelectedOptions.map(v => (
          <Badge
            key={v.id}
            variant="outline"
            color={getColorForOption(v.id, columns)}
            className="w-fit h-6 rounded-lg border-none"
          >
            <Ellipsis asChild lineClamp={1} hasTooltip>
              <Text variant="body-xs">{v.value}</Text>
            </Ellipsis>
          </Badge>
        ))
      ) : (
        <span className="text-muted-foreground text-sm">No value</span>
      )}
    </div>
  );

  return (
    <div className="w-full h-full" onClick={e => e.stopPropagation()}>
      <Multiselect
        options={multiselectOptions}
        selected={selectedValues}
        onChange={handleChange}
        trigger={dropdownTrigger}
        showClearAll={false}
        sideOffset={4}
        onOpenChange={setIsOpen}
      />
    </div>
  );
};

export const EditableLinkCell: FC<EditableCellProps> = ({ cell }) => {
  const handleClick = () => {
    if (cell.textValue) {
      window.open(cell.textValue, '_blank');
    }
  };

  return (
    <CellDisplayWrapper onClick={handleClick}>
      <Text variant="body-sm" className="text-blue-600 hover:underline">
        <a href={cell.textValue ?? undefined} target="_blank">
          {cell.textValue || '-'}
        </a>
      </Text>
    </CellDisplayWrapper>
  );
};
